"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User as FirebaseUser,
} from "firebase/auth";
import {
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import * as Sentry from "@sentry/react";
import { resetAnalytics } from "@/lib/analytics";
import { getFirebaseAuth, getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { userDocSchema, type UserDoc } from "@/lib/schemas";
import { resetRegistry } from "@/lib/realtime/registry";

type AuthState = {
  loading: boolean;
  firebaseUser: FirebaseUser | null;
  userDoc: UserDoc | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOutAll: () => Promise<void>;
  /** Wysyła e-mail z linkiem do resetu hasła. */
  resetPassword: (email: string) => Promise<void>;
  /** Wysyła e-mail weryfikacyjny do aktualnie zalogowanego usera. */
  sendVerificationEmail: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // undefined = jeszcze nie wiemy (przed pierwszym onAuthStateChanged)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null | undefined>(undefined);
  const [userDocState, setUserDocState] = useState<UserDoc | null>(null);
  const [userDocLoaded, setUserDocLoaded] = useState(false);

  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setFirebaseUser(u);
      // Sentry user context: tylko uid (bez email/displayName) — minimization
      // principle + RODO Art. 8 (dzieci). Sentry pokaże który użytkownik miał
      // crash bez ujawniania PII w issue panel.
      if (u) {
        Sentry.setUser({ id: u.uid });
      } else {
        Sentry.setUser(null);
      }
      if (!u) {
        setUserDocState(null);
        setUserDocLoaded(true);
      } else {
        setUserDocLoaded(false);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!firebaseUser) {
      // Po signOut wyczyść tagi Sentry — kolejny user na tym samym device
      // nie powinien być zaliczony do poprzedniej rodziny przy crashy.
      Sentry.setTag("householdId", undefined);
      Sentry.setTag("role", undefined);
      return;
    }
    const db = getDb();
    const ref = doc(db, "users", firebaseUser.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const parsed = userDocSchema.safeParse({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            ...snap.data(),
          });
          setUserDocState(parsed.success ? parsed.data : null);
          // Sentry tags: hid + role żeby filtrować errory per rodzina / per rola.
          // householdId pozwala mi zobaczyć "czy ten crash hituje tylko 1 HH
          // czy wszystkie"; role pozwala oddzielić child vs parent UX bugs.
          if (parsed.success) {
            Sentry.setTag("householdId", parsed.data.householdId);
            Sentry.setTag("role", parsed.data.role);
          }
        } else {
          setUserDocState(null);
          Sentry.setTag("householdId", undefined);
          Sentry.setTag("role", undefined);
        }
        setUserDocLoaded(true);
      },
      () => setUserDocLoaded(true),
    );
    return () => unsub();
  }, [firebaseUser]);

  const loading = firebaseUser === undefined || !userDocLoaded;

  const value = useMemo<AuthState>(
    () => ({
      loading,
      firebaseUser: firebaseUser ?? null,
      userDoc: userDocState,
      signIn: async (email, password) => {
        await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
      },
      signInWithGoogle: async () => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: "select_account" });
        await signInWithPopup(getFirebaseAuth(), provider);
      },
      signOutAll: async () => {
        const auth = getFirebaseAuth();
        const uid = auth.currentUser?.uid;
        const hid = userDocState?.householdId;
        // Best-effort cleanup FCM tokenów (audyt MED-2): bez tego push
        // do uid wylogowanego usera nadal trafia na shared-device, więc
        // kolejny user (np. drugie dziecko logujące się na ten sam
        // telefon) widzi obce notyfikacje. Cleanup wykonujemy PRZED
        // signOut bo po wylogowaniu rules `delete: resource.data.userId
        // == request.auth.uid` przestaną działać.
        if (uid && hid) {
          try {
            const db = getDb();
            const tokensSnap = await getDocs(
              query(
                refCol(db, paths.fcmTokens(hid)),
                where("userId", "==", uid),
              ),
            );
            await Promise.allSettled(
              tokensSnap.docs.map((d) => deleteDoc(d.ref)),
            );
          } catch {
            // ignore — signOut idzie zawsze, nawet jeśli cleanup padł
          }
        }
        await signOut(auth);
        resetRegistry();
        // Nie sklejaj kolejnego usera na shared device z poprzednim.
        resetAnalytics();
      },
      resetPassword: async (email) => {
        await sendPasswordResetEmail(getFirebaseAuth(), email.trim(), {
          url: "https://app.sianko.pl/auth-action",
          handleCodeInApp: false,
        });
      },
      sendVerificationEmail: async () => {
        const u = getFirebaseAuth().currentUser;
        if (!u) throw new Error("Nie zalogowano");
        await sendEmailVerification(u, {
          url: "https://app.sianko.pl/auth-action",
          handleCodeInApp: false,
        });
      },
    }),
    [loading, firebaseUser, userDocState],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth musi być wewnątrz <AuthProvider>");
  return ctx;
}
